import { OverflowList } from "@theme/overflow-list";
import VariantPicker from "@theme/variant-picker";
import { Component } from "@theme/component";
import {
  debounce,
  fetchConfig,
  getViewParameterValue,
  isDesktopBreakpoint,
  mediaQueryLarge,
  yieldToMainThread,
} from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartUpdateEvent,
  VariantSelectedEvent,
  VariantUpdateEvent,
  SlideshowSelectEvent,
} from "@theme/events";
import { morph, MORPH_OPTIONS } from "@theme/morph";
import { Slideshow } from "./slideshow.js";
import { syncQuickAddModalGalleryControls } from "./quick-add-modal-media.js";

/**
 * Singleton that fetches /cart.js once and keeps a live Set of product IDs in cart.
 * Updated whenever a cart:update event carries a full cart payload.
 */
const cartProductIdCache = {
  /** @type {Set<string> | null} */
  _ids: null,
  /** @type {Promise<Set<string>> | null} */
  _promise: null,

  /** Returns a promise that resolves to the current cart product-ID set. */
  get() {
    if (this._ids !== null) return Promise.resolve(this._ids);
    if (!this._promise) {
      this._promise = fetch("/cart.js")
        .then((r) => r.json())
        .then((cart) => {
          this._ids = new Set(
            (cart.items || []).map((i) => String(i.product_id)),
          );
          this._promise = null;
          return this._ids;
        })
        .catch(() => {
          this._ids = new Set();
          this._promise = null;
          return this._ids;
        });
    }
    return this._promise;
  },

  /** Updates the cache from a full cart object (items array required). */
  updateFromCart(cart) {
    if (cart && Array.isArray(cart.items)) {
      this._ids = new Set(cart.items.map((i) => String(i.product_id)));
    }
  },
};

/**
 * `cart:update` can carry either a full cart payload (/cart.js, /cart/change.js)
 * or a partial add response (/cart/add.js). We only want to sync every card badge
 * from full-cart payloads; partial payloads can contain only newly added items.
 * @param {any} cart
 * @returns {boolean}
 */
function isFullCartPayload(cart) {
  return (
    cart &&
    Array.isArray(cart.items) &&
    typeof cart.item_count === "number" &&
    typeof cart.total_price === "number"
  );
}

/**
 * Quick-add modal is rendered inside `product-card`. Clicks on its contents bubble to the card;
 * we must not run card navigation or view transitions for those clicks.
 * Uses composedPath so it still works when delegated handlers proxy `event.target` to the card.
 * @param {Event} event
 * @returns {boolean}
 */
function isEventFromOpenQuickAddDialog(event) {
  const path =
    typeof event.composedPath === "function" ? event.composedPath() : [];
  return path.some(
    (node) =>
      node instanceof HTMLDialogElement &&
      node.open &&
      node.closest("quick-add-dialog"),
  );
}

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute("data-product-transition") === "true";
  }

  get featuredMediaUrl() {
    return this.getAttribute("data-featured-media-url");
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    if (isEventFromOpenQuickAddDialog(event)) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest(
        'button, input, label, select, [tabindex="1"]',
      );
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (
      !cardGallery ||
      !cardGallery.hasAttribute("data-view-transition-to-main-product")
    )
      return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest("slideshow-slide")?.getAttribute("aria-hidden") ===
          "false",
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement)
      this.#setImageSrcset(activeImage);

    cardGallery.setAttribute(
      "data-view-transition-type",
      "product-image-transition",
    );
    cardGallery.setAttribute("data-view-transition-triggered", "true");
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: "ease-in-out",
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? "";
      };
    }
  }
}

if (!customElements.get("product-card-link")) {
  customElements.define("product-card-link", ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ["productCardLink"];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, "_blank");
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement))
      throw new Error("Product card link not found");
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected,
    );
    this.addEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideshowSelect,
    );
    mediaQueryLarge.addEventListener("change", this.#handleQuickAdd);

    this.addEventListener("click", this.navigateToProduct);
    this.addEventListener("click", this.#onRemoveFromCartClick);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAddBadge);
    this.#initInCartBadge();

    // Preload all gallery images when card enters viewport (async for all products, no hover-triggered loading)
    requestAnimationFrame(() => {
      this.#observeAndPreloadGalleryImages();
    });
  }

  /** @type {IntersectionObserver | null} */
  #galleryPreloadObserver = null;

  #observeAndPreloadGalleryImages() {
    const cardGallery =
      this.refs.cardGallery || this.querySelector(".card-gallery");
    if (!cardGallery) return;

    const rootMargin = "100px";
    const marginPx = parseInt(rootMargin, 10) || 100;

    const checkInViewportAndPreload = () => {
      const rect = cardGallery.getBoundingClientRect();
      if (rect.top < window.innerHeight + marginPx && rect.bottom > -marginPx) {
        this.#preloadAllGalleryImages();
        return true;
      }
      return false;
    };

    if (checkInViewportAndPreload()) return;

    if (this.#galleryPreloadObserver) return;

    this.#galleryPreloadObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#preloadAllGalleryImages();
          this.#galleryPreloadObserver?.unobserve(entry.target);
        }
      },
      { rootMargin: `${rootMargin} 0px`, threshold: 0 },
    );
    this.#galleryPreloadObserver.observe(cardGallery);
  }

  #preloadAllGalleryImages() {
    const cardGallery =
      this.refs.cardGallery || this.querySelector(".card-gallery");
    cardGallery?.querySelectorAll('img[loading="lazy"]')?.forEach((img) => {
      img.removeAttribute("loading");
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.navigateToProduct);
    this.removeEventListener("click", this.#onRemoveFromCartClick);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAddBadge);
    this.#galleryPreloadObserver?.disconnect();
    this.#galleryPreloadObserver = null;
  }

  /** Checks the live Shopify cart on mount and sets the badge if the product is already in cart. */
  async #initInCartBadge() {
    const ids = await cartProductIdCache.get();
    this.#syncBadgeFromIds(ids);
  }

  /**
   * Handles every cart:update event (add, update, remove).
   * When the event carries a full cart payload we update the cache and sync all badges.
   * When it only carries productId (add-only) we immediately show the badge on the matching card.
   * @param {CartAddEvent} event
   */
  #handleCartAddBadge = (event) => {
    if (event.detail?.data?.didError) return;

    const cart = event.detail?.resource;
    if (isFullCartPayload(cart)) {
      cartProductIdCache.updateFromCart(cart);
      this.#syncBadgeFromIds(cartProductIdCache._ids);
      return;
    }

    const addedProductId = event.detail?.data?.productId;
    if (addedProductId && String(addedProductId) === String(this.dataset.productId)) {
      this.setAttribute("data-in-cart", "true");
      if (cartProductIdCache._ids) {
        cartProductIdCache._ids.add(String(this.dataset.productId));
      }
    }
  };

  /**
   * Shows or hides the in-cart badge based on a Set of product IDs currently in the cart.
   * @param {Set<string> | null} ids
   */
  #syncBadgeFromIds(ids) {
    if (!ids) return;
    if (ids.has(String(this.dataset.productId))) {
      this.setAttribute("data-in-cart", "true");
    } else {
      this.removeAttribute("data-in-cart");
    }
  };

  /**
   * Removes all cart lines for this product (all variants) from the mini-cart flow.
   * @param {MouseEvent} event
   */
  #onRemoveFromCartClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(".card-gallery__remove-from-cart");
    if (!button || !this.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();

    const productId = this.dataset.productId;
    if (!productId || this.dataset.placeholder === "true") return;

    button.disabled = true;

    try {
      const cartRes = await fetch("/cart.js");
      const cart = await cartRes.json();
      const items = cart.items || [];

      /** @type {Record<string, number>} */
      const updates = {};
      for (const item of items) {
        if (String(item.product_id) === String(productId)) {
          updates[String(item.variant_id)] = 0;
        }
      }

      if (Object.keys(updates).length === 0) {
        this.removeAttribute("data-in-cart");
        if (cartProductIdCache._ids) {
          cartProductIdCache._ids.delete(String(productId));
        }
        return;
      }

      /** @type {string[]} */
      const sectionIds = [];
      document.querySelectorAll("cart-items-component").forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) {
          sectionIds.push(el.dataset.sectionId);
        }
      });

      /** @type {{ updates: Record<string, number>; sections?: string; sections_url?: string }} */
      const payload = {
        updates,
        sections_url: window.location.pathname,
      };
      if (sectionIds.length > 0) {
        payload.sections = sectionIds.join(",");
      }

      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig("json", { body: JSON.stringify(payload) }),
      );
      const data = await response.json();

      if (data.errors) {
        console.error(data.errors);
        return;
      }

      cartProductIdCache.updateFromCart(data);
      this.#syncBadgeFromIds(cartProductIdCache._ids);

      this.dispatchEvent(
        new CartUpdateEvent(data, this.id || "product-card", {
          source: "product-card-remove-product",
          productId,
          itemCount: Number(data?.item_count) || 0,
          sections: data.sections,
        }),
      );
    } catch (error) {
      console.error(error);
    } finally {
      button.disabled = false;
    }
  };

  #preloadNextPreviewImage() {
    const currentSlide =
      this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling
      ?.querySelector('img[loading="lazy"]')
      ?.removeAttribute("loading");
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener("pointerenter", this.#fetchProductPageHandler);
    this.removeEventListener("focusin", this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener("pointerenter", this.#fetchProductPageHandler);
      this.addEventListener("focusin", this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#morphSectionRenderableParts(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute("data-no-swatch-selected");

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector(
      "swatches-variant-picker-component overflow-list",
    );
    const isActiveOverflowList = overflowList?.querySelector(
      '[slot="overflow"]',
    )
      ? true
      : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent("reflow", {
          bubbles: true,
          detail: {},
        }),
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const responseCard = event.detail.data.html?.querySelector("product-card");
    const containers = this.querySelectorAll(
      `product-price [ref='priceContainer']`,
    );
    const newContainers = responseCard?.querySelectorAll(
      `product-price [ref='priceContainer']`,
    );
    const priceContainer =
      containers.length >= 2 ? containers[1] : containers[0];
    const newPriceElement =
      newContainers && newContainers.length >= 2
        ? newContainers[1]
        : newContainers?.[0];

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Same fragments as in `sections/section-rendering-product-card.liquid`: inventory and SKU with `product_resource: product`.
   * @param {VariantUpdateEvent} event
   */
  #morphSectionRenderableParts(event) {
    const responseCard = event.detail.data.html?.querySelector("product-card");
    if (!(responseCard instanceof HTMLElement)) return;

    const selectors = [
      ".card-gallery",
      "product-inventory",
      "product-sku-component",
      ".buy-buttons-block",
    ];
    for (const sel of selectors) {
      const dest = this.querySelector(`.product-card__content ${sel}`);
      const src = responseCard.querySelector(`.product-card__content ${sel}`);
      if (!(dest instanceof HTMLElement) || !(src instanceof HTMLElement)) {
        continue;
      }
      try {
        morph(dest, src, MORPH_OPTIONS);
        if (sel === ".card-gallery") {
          queueMicrotask(() => {
            const slideshow = dest.querySelector("slideshow-component");
            if (slideshow instanceof Slideshow) {
              slideshow.reinitializeSlidesAfterContentChange();
            }
          });
        }
      } catch {
        // ignore morph errors from structural drift
      }
    }

    this.#morphProductTitleParagraphFromSection(responseCard);
    this.#morphQuickAddFromSection(responseCard);

    const hostSectionId = this.closest(".shopify-section")?.id;
    if (hostSectionId) {
      this.querySelectorAll("product-form-component[data-section-id]").forEach(
        (el) => {
          if (el instanceof HTMLElement) el.dataset.sectionId = hostSectionId;
        },
      );
      this.querySelectorAll("volume-pricing[data-section-id]").forEach((el) => {
        if (el instanceof HTMLElement) el.dataset.sectionId = hostSectionId;
      });
    }
  }

  /**
   * Quick-add and the modal are rendered in Liquid on the card; the `section-rendering-product-card` response updates them when the linked product changes.
   * @param {HTMLElement} responseCard
   */
  #morphQuickAddFromSection(responseCard) {
    const cardLink = this.querySelector(
      'a[ref="productCardLink"], a.product-card__link',
    );

    const srcHelper = responseCard.querySelector(
      ".product-card__quick-add-helper",
    );
    const destHelper = this.querySelector(".product-card__quick-add-helper");

    if (srcHelper && destHelper) {
      const srcQa = srcHelper.querySelector("quick-add-component");
      const destQa = destHelper.querySelector("quick-add-component");
      if (srcQa instanceof HTMLElement && destQa instanceof HTMLElement) {
        try {
          morph(destQa, srcQa, MORPH_OPTIONS);
        } catch {
          // ignore morph errors
        }
      }
    } else if (srcHelper && !destHelper && cardLink) {
      cardLink.before(document.importNode(srcHelper, true));
    } else if (!srcHelper && destHelper) {
      destHelper.remove();
    }

    const srcDialog = responseCard.querySelector("quick-add-dialog");
    const destDialog = this.querySelector("quick-add-dialog");
    let touchedQuickAddModal = false;

    if (srcDialog && destDialog) {
      if (srcDialog.id) destDialog.id = srcDialog.id;
      const pid = srcDialog.getAttribute("data-product-id");
      if (pid != null) destDialog.setAttribute("data-product-id", pid);

      const srcContent = srcDialog.querySelector(".quick-add-modal__content");
      const destContent = destDialog.querySelector(".quick-add-modal__content");
      if (
        srcContent instanceof HTMLElement &&
        destContent instanceof HTMLElement
      ) {
        try {
          morph(destContent, srcContent, MORPH_OPTIONS);
        } catch {
          // ignore morph errors
        }
      }
      touchedQuickAddModal = true;
    } else if (srcDialog && !destDialog && cardLink) {
      cardLink.before(document.importNode(srcDialog, true));
      touchedQuickAddModal = true;
    } else if (!srcDialog && destDialog) {
      destDialog.remove();
    }

    if (touchedQuickAddModal) {
      queueMicrotask(() => {
        syncQuickAddModalGalleryControls();
        const dl = this.querySelector("quick-add-dialog");
        const ss = dl?.querySelector("slideshow-component");
        if (ss instanceof Slideshow) ss.reinitializeSlidesAfterContentChange();
      });
    }
  }

  /**
   * Title: in the theme, `a[ref=productTitleLink]` often wraps `.text-block` then `p`. The section returns the same ref and `p`;
   * morph only the paragraph; leave outer editor classes untouched.
   * @param {HTMLElement} responseCard
   */
  #morphProductTitleParagraphFromSection(responseCard) {
    const srcA = responseCard.querySelector(
      '.product-card__content a[ref="productTitleLink"]',
    );
    const destA = this.querySelector(
      '.product-card__content a[ref="productTitleLink"]',
    );
    if (!(srcA instanceof HTMLElement) || !(destA instanceof HTMLElement)) {
      return;
    }
    const srcP = srcA.querySelector("p");
    const destP = destA.querySelector("p");
    if (!(srcP instanceof HTMLElement) || !(destP instanceof HTMLElement)) {
      return;
    }
    try {
      morph(destP, srcP, MORPH_OPTIONS);
    } catch {
      destP.textContent = srcP.textContent;
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard =
      event.detail.data.html?.querySelector("product-card");
    if (!(responseProductCard instanceof HTMLElement)) return;

    const npid = responseProductCard.getAttribute("data-product-id");
    if (npid) this.setAttribute("data-product-id", npid);

    const anchorElement = responseProductCard.querySelector(
      'a.product-card__link[ref="productCardLink"]',
    );
    const featuredMediaUrl = responseProductCard.getAttribute(
      "data-featured-media-url",
    );

    if (featuredMediaUrl) {
      this.setAttribute("data-featured-media-url", featuredMediaUrl);
    } else if (this.productTransitionEnabled) {
      this.removeAttribute("data-featured-media-url");
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute("href")?.trim() === "") return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll("input:checked")
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === "true");
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const buttons = this.querySelectorAll(
      '.buy-buttons-block .add-to-cart__button button[type="submit"], .buy-buttons-block button[name="add"], .buy-buttons-block .buy-buttons__choose-variant button[type="button"]',
    );
    for (const btn of buttons) {
      if (btn instanceof HTMLButtonElement) btn.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId =
      this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute("variant-image") == null) continue;

        slide.hidden = slide.getAttribute("slide-id") !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll("input[data-variant-id]");
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector("swatches-variant-picker-component");
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== "mouse") return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== "mouse") return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute("slide-id");
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    if (isEventFromOpenQuickAddDialog(event)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute("data-no-navigation")) return;

    const interactiveElement = event.target.closest(
      'button, input, label, select, [tabindex="1"]',
    );

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute("id");
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest(
      'results-list[infinite-scroll="true"]',
    );
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest("li");
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set("page", parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, "", url.toString());
      });
    }

    const targetLink = event.target.closest("a");
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest("product-card");

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleCardVariantUrlUpdate.bind(this),
    );
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.dataset.linkedVariationPicker === "true") {
      this.pendingVariantId = null;
      return;
    }
    if (
      this.pendingVariantId &&
      this.parentProductCard instanceof ProductCard
    ) {
      const currentUrl = new URL(
        this.parentProductCard.refs.productCardLink.href,
      );
      currentUrl.searchParams.set("variant", this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isLinkedPicker = this.dataset.linkedVariationPicker === "true";
    const isSwatchInput =
      event.target instanceof HTMLInputElement &&
      event.target.name?.includes("-swatch");
    const clickedSwatch = event.target;
    const availableCount = parseInt(
      clickedSwatch.dataset.availableCount || "0",
    );
    const firstAvailableVariantId =
      clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Linked products: skip — otherwise fetch targets the card's data-product-url + variant, not the swatch target product.
    if (
      !isLinkedPicker &&
      isSwatchInput &&
      availableCount > 0 &&
      firstAvailableVariantId
    ) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split("?")[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("variant", firstAvailableVariantId);
      url.searchParams.set("section_id", "section-rendering-product-card");

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    if (
      isLinkedPicker &&
      this.dataset.linkedSwatchesFullPage === "true" &&
      event.target instanceof HTMLInputElement
    ) {
      const rawUrl = event.target.dataset.connectedProductUrl;
      if (rawUrl) {
        const pathname = rawUrl.split("?")[0];
        const url = new URL(pathname, window.location.origin);
        const variantId = event.target.dataset.variantId;
        if (variantId) url.searchParams.set("variant", variantId);
        const viewParamValue = getViewParameterValue();
        if (viewParamValue) url.searchParams.set("view", viewParamValue);
        window.location.assign(url.href);
        return;
      }
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get("swatches-variant-picker-component")) {
  customElements.define(
    "swatches-variant-picker-component",
    SwatchesVariantPickerComponent,
  );
}
